import {
  Button,
  Divider,
  Form,
  Icons,
  InputNumber,
  Select,
  Space,
  Typography,
  notification,
} from '@pankod/refine-antd';
import { ITransferProductState } from 'pages/stock-transfers';
import { useCallback, useEffect, useState } from 'react';
import {
  getVariantLotsByWarehouseAndVariantIds,
  InventoryLotWithVariant,
} from 'services/inventory';
import { IVariant, getVariant } from 'services/variant';
import { mergedVariantLot } from 'utilities/shared';
import styles from './style.module.css';
import { getSearchData, SearchIndex } from 'services/meilisearch';

type AddProductFormProps = {
  warehouseId: string | undefined;
  transferProducts: ITransferProductState[];
  onSubmit: (product: ITransferProductState) => void;
};

type IOption = {
  id: string;
  name: string;
  sku: string;
  quantity: null | number;
};

const { Text } = Typography;
let timeout: ReturnType<typeof setTimeout> | null;
let currentValue: string;

const fetchOptions = (value: string, callback: Function) => {
  if (timeout) {
    clearTimeout(timeout);
    timeout = null;
  }
  currentValue = value;

  const getOptions = async () => {
    const data = await getSearchData(SearchIndex.ProductVariant, value, { limit: 100 });

    if (currentValue === value && value !== '') {
      const options = data
        .map(
          (p) =>
            ({
              id: p.product_variant_id,
              sku: p.sku,
              name: p.name,
              quantity: null,
            } as IOption)
        )
        .filter((op) => {
          const lowerCasedValue = value.toLowerCase();
          return (
            op.name.toLowerCase().includes(lowerCasedValue) ||
            op.sku.toLowerCase().includes(lowerCasedValue)
          );
        });
      callback(options);
    }
  };

  timeout = setTimeout(getOptions, 300);
};

export const AddProductForm: React.FC<AddProductFormProps> = (props) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedOption, setSelectedOption] = useState<IVariant | null>(null);
  const [searchText, setSearchText] = useState<string>('');
  const [options, setOptions] = useState<IOption[]>([]);
  const [lots, setLots] = useState<InventoryLotWithVariant[]>([]);

  const [form] = Form.useForm();

  const fetchProductsQuantity = useCallback(async (warehouseId: string, ids: string[]) => {
    try {
      const response = await getVariantLotsByWarehouseAndVariantIds(warehouseId, ids);
      const products = mergedVariantLot(response);
      setLots(response);
      return products;
    } catch (err: any) {
      notification.error({
        message: 'Failed',
        description: err.message,
      });
      return [];
    }
  }, []);

  useEffect(() => {
    form.setFields([
      {
        name: 'variant',
        value: selectedOption?.productVariantId,
      },
      { name: 'unitPrice', value: selectedOption?.sellingPrice },
    ]);
  }, [form, selectedOption]);

  const handleProductOptionSearch = (newValue: string) => {
    fetchOptions(newValue, async (data: IOption[]) => {
      setOptions(data);

      if (props.warehouseId && data.length) {
        const ids = data.map((p) => p.id);
        const products = await fetchProductsQuantity(props.warehouseId, ids);
        const productsMap = new Map<string, (typeof products)[0]>();
        products.forEach((p) => productsMap.set(p.id, { ...p }));

        const options = data.map((option) => {
          const product = productsMap.get(option.id);
          if (product) {
            return {
              ...option,
              quantity: product.quantity,
            };
          } else {
            return {
              ...option,
              quantity: 0,
            };
          }
        });

        setOptions(options);
      }
    });
  };

  const handleProductOptionChange = (newValue: string) => {
    setSearchText(newValue);
  };

  const renderedProductOptions = options.map((p) => {
    // Option is disabled when product has quantity field and quantity is 0 or null
    const color = p.quantity === 0 || p.quantity === null ? '#A9A9A9' : '#626262';
    const title = `${p.sku} - ${p.name} (${p.quantity === null ? '⌛' : p.quantity})`;

    return {
      label: (
        <Typography.Text key={p.id} style={{ color }} title={title}>
          {`${p.sku} - ${p.name}`}{' '}
          {p.quantity === null && <Icons.LoadingOutlined style={{ marginLeft: '0.3rem' }} />}
          {typeof p.quantity === 'number' && `(${p.quantity})`}
        </Typography.Text>
      ),
      value: p.id,
      disabled: p.quantity === null || p.quantity === 0,
    };
  });

  const onSelectVariant = async (id: string) => {
    setIsLoading(true);
    form.resetFields(['quantity']);

    try {
      const variant = await getVariant(id);
      setSelectedOption(variant);
      form.setFields([{ name: 'unitPrice', value: variant.sellingPrice }]);
    } catch (err: any) {
      notification.error({
        message: 'Failed',
        description: err.message,
      });
    }
    setIsLoading(false);
  };

  const renderedAvailableStock = !selectedOption ? null : (
    <ul>
      {lots
        .filter((lot) => lot.productVariant.id === selectedOption.productVariantId)
        .map((lot) => {
          return lot.quantity > 0 ? (
            <li key={lot.id}>
              <Text>
                {`${lot.quantity} units available | Bought @ BDT ${lot.unitPrice} on
                ${new Date(lot.lotEntryDate).toLocaleDateString('en-GB')}`}
              </Text>
            </li>
          ) : null;
        })}
    </ul>
  );

  const attributes: {
    attributeId: string;
    attributeName: string;
    attributeType: 'text' | 'float' | 'boolean' | 'dropdown';
    value: string;
  }[] = [];
  const templates = selectedOption?.productTemplates || [];
  templates.forEach((template) => {
    const { productTemplateAttributes } = template;
    attributes.push(...productTemplateAttributes);
  });

  const attributeElements = attributes.map((attribute) => (
    <div
      key={attribute.attributeId}
      style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
    >
      <Typography.Text strong>{attribute.attributeName.toUpperCase()} : </Typography.Text>
      <Typography.Text>{attribute.value}</Typography.Text>
    </div>
  ));

  const customFields = selectedOption?.customFields ?? [];
  const customFieldElements = customFields.map((field, idx) => {
    const key = Object.keys(field)[0];
    const value = field[key];

    return (
      <div
        key={idx}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
      >
        <Typography.Text strong>{key.toUpperCase()} : </Typography.Text>
        <Typography.Text>{value}</Typography.Text>
      </div>
    );
  });

  const onFinish = (values: any) => {
    if (!selectedOption) return;

    const variantId: string = values.variant;
    const quantity: number = values.quantity;

    const totalQuantity = lots
      .filter((item) => item.productVariant.id === variantId)
      .reduce((acc, lot) => {
        acc += lot.quantity;
        return acc;
      }, 0);

    const isVariantExist = !!props.transferProducts.find(
      (product) => product.productVariantId === variantId
    );
    if (isVariantExist) {
      form.setFields([
        {
          name: 'variant',
          errors: [`Product is already added.`],
        },
      ]);
      return;
    }

    if (quantity === 0 || quantity > totalQuantity) {
      form.setFields([
        {
          name: 'quantity',
          errors: [`Quantity needs to be between 1 and ${totalQuantity}`],
        },
      ]);
      return;
    }

    props.onSubmit({ ...selectedOption, quantity });
    form.resetFields();
    setSelectedOption(null);
  };

  return (
    <Form
      layout="vertical"
      initialValues={{
        isActive: true,
      }}
      form={form}
      onFinish={onFinish}
    >
      <Form.Item
        label="Product"
        name="variant"
        rules={[{ required: true, message: 'Please select a product' }]}
      >
        <Select
          showSearch
          placeholder="Select a product"
          value={searchText}
          defaultActiveFirstOption={false}
          showArrow={false}
          filterOption={false}
          onSearch={handleProductOptionSearch}
          onChange={handleProductOptionChange}
          onSelect={onSelectVariant}
          options={renderedProductOptions}
        />
      </Form.Item>

      {selectedOption && (
        <>
          <Divider />
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography.Text strong>PARENT PRODUCT:</Typography.Text>
              <Typography.Text code>{selectedOption.productName}</Typography.Text>
            </div>

            {attributeElements}
            {customFieldElements}

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography.Text strong>PURCHASE PRICE:</Typography.Text>
              <Typography.Text>{selectedOption.purchasePrice}</Typography.Text>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography.Text strong>SELLING PRICE:</Typography.Text>
              <Typography.Text>{selectedOption.sellingPrice}</Typography.Text>
            </div>
          </div>
          <Divider />
        </>
      )}

      <Form.Item
        label="Quantity"
        name="quantity"
        rules={[
          {
            required: true,
            type: 'integer',
            min: 1,
            message: 'Quantity must be a positive integer value',
          },
        ]}
      >
        <InputNumber style={{ width: '100%' }} />
      </Form.Item>

      <Space direction="vertical">
        <Text className={styles['stock__text']}>* Available Stock</Text>
        {renderedAvailableStock}
      </Space>

      <Form.Item wrapperCol={{ span: 4, offset: 20 }}>
        <Button type="primary" htmlType="submit" disabled={isLoading}>
          Add
        </Button>
      </Form.Item>
    </Form>
  );
};
